import objectFitImages from 'object-fit-images';
import 'lazysizes';
import GOOGLE_MAP_STYLES from './wp-map-styles';

class WPMapStatic extends HTMLElement {
  /**
   * Tag name of element
   * @readonly
   * @static
   */
  static get is() {
    return 'wp-map-static';
  }

  /**
   * Getter for `address` attribute
   */
  get address() {
    return this.hasAttribute('address') ? this.getAttribute('address') : '';
  }

  /**
   * Setter for `address` attribute
   */
  set address(value) {
    if (value) {
      this.setAttribute('address', value);
    } else {
      this.removeAttribute('address');
    }
  }

  get zoom() {
    return this.hasAttribute('zoom') ? parseInt(this.getAttribute('zoom')) : 17;
  }

  set zoom(value) {
    this.setAttribute('zoom', parseInt(value));
  }

  get width() {
    return this.hasAttribute('width')
      ? parseInt(this.getAttribute('width'))
      : 512;
  }

  set width(value) {
    this.setAttribute('width', parseInt(value));
  }

  set height(value) {
    this.setAttribute('height', parseInt(value));
  }

  get height() {
    return this.hasAttribute('height')
      ? parseInt(this.getAttribute('height'))
      : 512;
  }

  /**
   * Getter for `api-key` attribute
   */
  get apiKey() {
    return this.hasAttribute('api-key') ? this.getAttribute('api-key') : '';
  }

  /**
   * Setter for `api-key` attribute
   */
  set apiKey(value) {
    if (value) {
      this.setAttribute('api-key', value);
    } else {
      this.removeAttribute('api-key');
    }
  }

  /**
   * Getter for `animation` attribute
   */
  get animation() {
    return this.hasAttribute('animation')
      ? this.getAttribute('animation')
      : 'fadeIn';
  }

  /**
   * Setter for `animation` attribute
   */
  set animation(value) {
    if (value) {
      this.setAttribute('animation', value);
    } else {
      this.removeAttribute('animation');
    }
  }

  /**
   * Getter for `latitude` attribute
   */
  get latitude() {
    return this.hasAttribute('latitude')
      ? parseFloat(this.getAttribute('latitude'))
      : 0;
  }

  /**
   * Setter for `latitude` attribute
   */
  set latitude(value) {
    this.setAttribute('latitude', parseFloat(value));
  }

  /**
   * Getter for `longitude` attribute
   */
  get longitude() {
    return this.hasAttribute('longitude')
      ? parseFloat(this.getAttribute('longitude'))
      : 0;
  }

  /**
   * Setter for `longitude` attribute
   */
  set longitude(value) {
    this.setAttribute('longitude', parseFloat(value));
  }

  /**
   * Getter for `icon` attribute
   */
  get icon() {
    return this.hasAttribute('icon') ? this.getAttribute('icon') : null;
  }

  /**
   * Setter for `icon` attribute
   */
  set icon(value) {
    if (String(value.length)) {
      this.setAttribute('icon', value);
    } else {
      this.removeAttribute('icon');
    }
  }

  /**
   * Getter for `loaded` attribute
   */
  get loaded() {
    return this.hasAttribute('loaded');
  }

  /**
   * Setter for `loaded` attribute
   */
  set loaded(value) {
    if (Boolean(value)) {
      this.setAttribute('loaded', '');
    } else {
      this.removeAttribute('loaded');
    }
  }

  /**
   * Getter for `fit` attribute
   */
  get fit() {
    return this.getAttribute('fit');
  }

  /**
   * Setter for `fit` attribute
   */
  set fit(value) {
    if (value) {
      this.setAttribute('fit', value);
    } else {
      this.removeAttribute('fit');
    }
  }

  /**
   * Getter for `position` attribute
   */
  get position() {
    return this.hasAttribute('position')
      ? this.getAttribute('position')
      : '50% 50%';
  }

  /**
   * Setter for `position` attribute
   */
  set position(value) {
    if (value) {
      this.setAttribute('position', value);
    } else {
      this.removeAttribute('position');
    }
  }

  /**
   * Lifecycle event when element is attached to DOM
   */
  connectedCallback() {
    this._img = this.querySelector('[data-img]');
    this.style.opacity = '0';

    // If loaded, return
    if (this.loaded) {
      return;
    }

    // If no latitude or longitude attribute defined, return
    if (!this.latitude || !this.longitude) {
      return;
    }

    // If no img defined, attach
    if (!this._img) {
      this.attach();
    }

    // Handle lazyload
    this._img.addEventListener('lazyloaded', (evt) => {
      if (this.animation) {
        this.classList.add(this.animation);
      }

      this.loaded = true;
    });
  }

  _applyObjectFit(img) {
    img.style.objectFit = this.fit;
    img.style.objectPosition = this.position;
    img.style.fontFamily = `
      object-fit: ${this.fit}; 
      object-position: ${this.position}`;
    img.style.height = '100%';
    img.style.width = '100%';
    img.style.display = 'block';

    objectFitImages(img);
  }

  attach() {
    if (this._img) {
      return;
    }

    const img = document.createElement('img');

    // img.srcset =
    //   'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
    // img.src = this.src;
    img.alt = this.address;
    img.className = 'lazyload';
    img.setAttribute('data-src', this._src);
    img.setAttribute('data-img', '');

    // Handle fit
    if (this.fit) {
      this._applyObjectFit(img);
    }

    this.insertBefore(img, this.firstChild);
    this._img = img;
  }

  get _src() {
    return `https://maps.googleapis.com/maps/api/staticmap?zoom=${this.zoom}&size=${this.width}x${this.height}&markers=icon:${this.icon}|${this.latitude},${this.longitude}&key=${this.apiKey}${WPMapStatic.STYLES}`;
  }

  static get STYLES() {
    let styles = '';

    GOOGLE_MAP_STYLES.forEach((style) => {
      styles += `&style=`;

      styles += `feature:${style.featureType ? style.featureType : 'all'}|`;
      styles += `element:${style.elementType ? style.elementType : 'all'}|`;

      style.stylers.forEach((obj) => {
        Object.keys(obj).forEach((prop) => {
          styles += `${prop}:${obj[prop]}|`;
        });
      });

      styles = styles.slice(0, -1);
    });

    return encodeURI(styles.replace(/#/gi, '0x'));
  }
}

customElements.define(WPMapStatic.is, WPMapStatic);
